<script>
export default {
  name: 'PackingOwnerChangeDialog',
  props: { orders: { type: Array, required: true } },
  emits: ['close', 'refresh'],
  data() {
    return {
      target: null,
      submitting: false,
      requestPayload: null,
      result: null,
      errorMessage: '',
      targetOptions: [
        { label: 'AONE', value: 'AONE' },
        { label: '각 주문의 소유 파트너', value: 'PARTNER' }
      ],
      columns: [
        { name: 'orderNo', label: 'Order No.', field: row => row.orderNo || row.orderBaseId, align: 'left' },
        { name: 'partnerName', label: '소유 파트너', field: row => row.partnerName || '-', align: 'left' },
        { name: 'previous', label: '변경 전', field: row => this.ownerLabel(row.previousPackingOwner), align: 'center' },
        { name: 'current', label: '현재', field: row => this.ownerLabel(row.packingOwner), align: 'center' },
        { name: 'result', label: '결과', field: 'result', align: 'center' },
        { name: 'message', label: '처리 사유', field: 'message', align: 'left' }
      ]
    }
  },
  methods: {
    ownerLabel(value) { return value === 'AONE' ? 'AONE' : value === 'PARTNER' ? '소유 파트너' : '-' },
    async apply() {
      if (this.submitting || this.result || !this.target || !this.orders.length) return
      this.submitting = true
      this.errorMessage = ''
      try {
        // Keep the exact request after an uncertain response. Retrying reads the
        // stored result; it must never become a new batch or a different target.
        if (!this.requestPayload) {
          this.requestPayload = {
            requestKey: window.crypto.randomUUID(), target: this.target,
            orderBaseIds: this.orders.map(row => Number(row.id))
          }
        }
        const response = await this.$http({
          method: 'POST', url: '/api/parcel/packing-owner-changes',
          data: this.requestPayload, timeout: 30000,
          name: '[ AXIOS / 통합조회 / 선택 일괄 포장 주체 변경 ]'
        })
        if (response?.data?.ok !== true || !Array.isArray(response?.data?.data?.results)) {
          throw new Error('처리 결과를 확인할 수 없습니다. 같은 요청으로 다시 확인해 주세요.')
        }
        this.result = response.data.data
      } catch (err) {
        this.errorMessage = err?.response?.data?.message
          || '처리 결과를 확인할 수 없습니다. 같은 요청으로 다시 확인해 주세요.'
      } finally {
        this.submitting = false
        this.$emit('refresh')
      }
    }
  }
}
</script>

<template>
  <q-card style="width: 960px; max-width: 96vw; max-height: 90vh;" class="column no-wrap">
    <q-card-section class="row items-center">
      <div class="text-h6">선택 일괄 포장 주체 변경</div>
      <q-space />
      <q-btn flat round dense icon="close" aria-label="닫기" :disable="submitting" @click="$emit('close')" />
    </q-card-section>
    <q-separator />
    <q-card-section class="col scroll">
      <div class="text-body2 q-mb-md">
        현재 페이지에서 선택한 주문 {{ orders.length }}건을 처리합니다.
        ‘각 주문의 소유 파트너’를 선택하면 주문마다 자신의 파트너가 포장합니다.
      </div>
      <q-select
        v-model="target" :options="targetOptions" label="변경할 포장 주체"
        outlined emit-value map-options :disable="submitting || !!requestPayload" />
      <div class="text-caption text-grey-8 q-mt-sm">
        오더접수 상태이며 포장 진행·출력 이력이 없는 주문만 변경합니다. 출력 선점·접수 확인 중인 주문은 제외합니다.
        콘솔 등록·포장중·완료·보류 건은 제외하며 이미 같은 주체인 주문은 그대로 둡니다.
      </div>
      <q-banner v-if="errorMessage" class="bg-red-1 text-negative q-mt-md" role="alert">
        {{ errorMessage }}
        <div class="text-caption">결과 확인 전에는 새 요청을 만들지 말고 ‘같은 요청 결과 확인’을 눌러 주세요.</div>
      </q-banner>
      <template v-if="result">
        <div class="row q-gutter-sm q-my-md" aria-live="polite">
          <q-badge color="positive">변경 {{ result.summary.changed }}건</q-badge>
          <q-badge color="grey-7">동일 {{ result.summary.unchanged }}건</q-badge>
          <q-badge color="negative">변경 불가 {{ result.summary.rejected }}건</q-badge>
        </div>
        <q-table flat bordered dense wrap-cells :rows="result.results" :columns="columns"
          row-key="orderBaseId" hide-pagination :pagination="{ rowsPerPage: 0 }">
          <template #body-cell-result="props">
            <q-td :props="props">
              <q-badge :color="props.value === 'CHANGED' ? 'positive' : props.value === 'UNCHANGED' ? 'grey-7' : 'negative'">
                {{ props.value === 'CHANGED' ? '변경' : props.value === 'UNCHANGED' ? '동일' : '변경 불가' }}
              </q-badge>
            </q-td>
          </template>
        </q-table>
        <div v-if="result.replayed" class="text-caption text-grey-7 q-mt-sm">앞서 처리한 같은 요청의 결과입니다.</div>
      </template>
    </q-card-section>
    <q-separator />
    <q-card-actions align="right">
      <q-btn flat :label="result ? '닫기' : '취소'" :disable="submitting" @click="$emit('close')" />
      <q-btn v-if="!result" color="primary" :label="requestPayload ? '같은 요청 결과 확인' : '선택 주문에 적용'"
        :loading="submitting" :disable="!target || !orders.length" @click="apply" />
    </q-card-actions>
  </q-card>
</template>
